import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Lightbulb, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const MAX_LEN = 2000;

/**
 * SuggestionForm — anonymous-friendly feature request card.
 * POSTs to /api/suggestions and swaps to a thank-you state on 200.
 *
 * Silently attaches the current React Router pathname as `page_context` so
 * admins can see where the user was when they raised the idea (never shown
 * to the user; no tracking beyond the current in-app route).
 */
export default function SuggestionForm() {
  const location = useLocation();
  const [text, setText] = useState("");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      toast.error("Tell us a bit more (at least 3 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/suggestions", {
        suggestion_text: trimmed,
        user_contact: contact.trim() || null,
        page_context: location.pathname || null,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        className="td-card p-5 sm:p-6 flex items-start gap-3 border-brand-primary/40 bg-brand-primary/5"
        data-testid="suggestion-thanks"
      >
        <div className="w-10 h-10 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
          <CheckCircle2 size={20} className="text-brand-primary" />
        </div>
        <div>
          <div className="font-heading text-lg text-brand-text">Thanks for helping improve the app!</div>
          <p className="text-sm text-brand-subtle mt-1 leading-relaxed">
            Ozzy just gave your idea a paw-shaped nod. If you left your details we&apos;ll reach out when it lands.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="td-card p-5 sm:p-6" onSubmit={onSubmit} data-testid="suggestion-form">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-brand-accent/15 flex items-center justify-center shrink-0">
          <Lightbulb size={18} className="text-brand-accent" />
        </div>
        <div className="min-w-0">
          <div className="td-section-label">Feedback</div>
          <div className="font-heading text-lg text-brand-text mt-0.5">Suggest a feature</div>
        </div>
      </div>
      <p className="text-sm text-brand-subtle mb-3">
        What would make TrailDog more useful on your next walk? We read every note.
      </p>
      <textarea
        data-testid="suggestion-text"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
        placeholder="e.g. Could you add tide times for coastal walks?"
        rows={4}
        disabled={submitting}
        className="w-full td-input resize-none"
        aria-label="Your suggestion"
      />
      <div className="flex items-center justify-between mt-1 text-[11px] text-brand-subtle">
        <span>Optional email or handle if you&apos;d like a reply</span>
        <span>{text.length}/{MAX_LEN}</span>
      </div>
      <input
        data-testid="suggestion-contact"
        type="text"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        placeholder="you@example.com or @handle"
        disabled={submitting}
        className="w-full td-input mt-2"
        aria-label="Your contact (optional)"
      />
      <button
        type="submit"
        data-testid="suggestion-submit"
        disabled={submitting || text.trim().length < 3}
        className="td-btn-primary mt-4 w-full !py-2.5 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send size={14} /> Send suggestion
          </>
        )}
      </button>
    </form>
  );
}
